import { useState, useEffect, useCallback } from 'react';
import { useRegion } from '../context/RegionContext';
import { enrichProductDefaults } from '../data/productDefaults';
import { applyPublishedCatalogChanges, mergeCatalogChanges, mergeCatalogWithChanges } from '../utils/localProductCatalog';

const publicBasePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const catalogueDataUrl = (filename) => `${publicBasePath}/data/${filename}`;

// Module-level cache — the catalog is fetched ONCE per browser tab session and
// shared by every page. Previously each page navigation re-downloaded
// products.json (344KB) AND dashboard-products.json (1.4MB, no-store), which is
// why clicking between pages felt slow, especially on mobile data.
let baseCatalogPromise = null;
let publishedCatalogPromise = null;

const getBaseCatalog = () => {
  if (!baseCatalogPromise) {
    baseCatalogPromise = fetch(catalogueDataUrl('products.json'), { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load products: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!data || !Array.isArray(data.products)) throw new Error('Invalid products data format');
        return data.products.map(enrichProductDefaults);
      });
    baseCatalogPromise.catch(() => { baseCatalogPromise = null; });
  }
  return baseCatalogPromise;
};

const getPublishedCatalog = () => {
  if (!publishedCatalogPromise) {
    publishedCatalogPromise = getBaseCatalog().then((baseProducts) =>
      fetch(catalogueDataUrl('dashboard-products.json'), { cache: 'no-cache' })
        .then((response) => (response.ok ? response.json().catch(() => null) : null))
        .then((json) => {
          if (!json) return baseProducts;
          const publishedChanges = applyPublishedCatalogChanges(json);
          return mergeCatalogWithChanges(baseProducts, publishedChanges).map(enrichProductDefaults);
        })
        .catch(() => baseProducts)
    );
    publishedCatalogPromise.catch(() => { publishedCatalogPromise = null; });
  }
  return publishedCatalogPromise;
};

export const useProducts = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { selectedProvince } = useRegion();

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      try {
        setLoading(true);
        setError(null);

        // Render the original catalogue first. Dashboard exports can contain high-resolution
        // image data and must never keep the 328 baseline items hidden while they are parsed.
        const baseProducts = await getBaseCatalog();
        if (cancelled) return;
        setAllProducts(mergeCatalogChanges(baseProducts));
        setError(null);
        setLoading(false);

        // Log for debugging in development
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Products loaded:', baseProducts.length);
        }

        // Apply the optional dashboard overlay afterwards. If it is unavailable or slow,
        // customers continue to see the baseline catalogue instead of an empty loading state.
        const publishedProducts = await getPublishedCatalog();
        if (cancelled) return;
        setAllProducts(mergeCatalogChanges(publishedProducts));
      } catch (err) {
        if (cancelled) return;
        console.error('❌ Error loading products:', err);
        setError(err.message);
        setAllProducts([]);
        setLoading(false);
      }
    };

    loadCatalog();
    return () => { cancelled = true; };
  }, []); // Empty dependency array - fetch once on mount (module cache shares across pages)

  useEffect(() => {
    const handleCatalogChange = () => setAllProducts((current) => mergeCatalogChanges(current));
    window.addEventListener('skyfurniture:catalog-changed', handleCatalogChange);
    return () => window.removeEventListener('skyfurniture:catalog-changed', handleCatalogChange);
  }, []);

  // Apply Province filter whenever selectedProvince or allProducts changes
  useEffect(() => {
    const filtered = allProducts.filter(product => product.availableProvinces.map(province => province.toLowerCase()).includes(selectedProvince.toLowerCase()));
    setProducts(filtered.map(product => ({ ...product, priceCAD: product.provincePrices?.[selectedProvince] ?? product.priceCAD })));
  }, [selectedProvince, allProducts]);

  /**
   * Get a single product by ID
   * @param {string|number} id - Product ID
   * @returns {Object|undefined} Product object or undefined if not found
   */
  const getProductById = useCallback((id) => {
    const product = allProducts.find(p => p.id === parseInt(id));
    if (!product) return undefined;
    return { ...product, priceCAD: product.provincePrices?.[selectedProvince] ?? product.priceCAD };
  }, [allProducts, selectedProvince]);

  /**
   * Get products filtered by category and optional subcategory
   * @param {string} category - Category slug (e.g., 'dressers')
   * @param {string|null} subcategory - Optional subcategory slug
   * @returns {Array} Filtered products array
   */
  const getProductsByCategory = useCallback((category, subcategory = null) => {
    return products.filter(p => {
      const categoryMatch = p.category?.toLowerCase() === category?.toLowerCase();
      
      if (subcategory) {
        const subcategoryMatch = p.subcategory?.toLowerCase() === subcategory?.toLowerCase();
        return categoryMatch && subcategoryMatch;
      }
      
      return categoryMatch;
    });
  }, [products]);

  /**
   * Get only in-stock products
   * @returns {Array} In-stock products array
   */
  const getInStockProducts = useCallback(() => {
    return products.filter(p => p.inStock === true);
  }, [products]);

  /**
   * Get out-of-stock products
   * @returns {Array} Out-of-stock products array
   */
  const getOutOfStockProducts = useCallback(() => {
    return products.filter(p => p.inStock === false);
  }, [products]);

  /**
   * Search products by query string
   * Searches across name, category, subcategory, and description
   * @param {string} query - Search query
   * @returns {Array} Matching products array
   */
  const searchProducts = useCallback((query) => {
    if (!query || query.trim() === '') {
      return products;
    }

    const searchLower = query.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    return products.filter(p => {
      const searchableText = [
        p.name || '',
        p.category || '',
        p.subcategory || '',
        p.description || ''
      ].join(' ').toLowerCase();
      
      const normalizedText = searchableText.replace(/-/g, ' ');
      
      // Check if ALL search words are found
      const allWordsMatch = searchWords.every(word => 
        normalizedText.includes(word) || searchableText.includes(word)
      );
      
      if (allWordsMatch) {
        return true;
      }
      
      // Fallback: check if entire phrase matches
      return normalizedText.includes(searchLower) || searchableText.includes(searchLower);
    });
  }, [products]);

  /**
   * Get products sorted by various criteria
   * @param {Array} productsList - Products array to sort (defaults to all products)
   * @param {string} sortBy - Sort criteria: 'price-low', 'price-high', 'name-asc', 'name-desc', 'newest', 'oldest'
   * @returns {Array} Sorted products array
   */
  const getSortedProducts = useCallback((productsList = products, sortBy = 'newest') => {
    const sorted = [...productsList];

    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => (a.priceCAD || 0) - (b.priceCAD || 0));
      
      case 'price-high':
        return sorted.sort((a, b) => (b.priceCAD || 0) - (a.priceCAD || 0));
      
      case 'name-asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      case 'name-desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      
      case 'oldest':
        return sorted.sort((a, b) => a.id - b.id);
      
      case 'newest':
      default:
        return sorted.sort((a, b) => b.id - a.id);
    }
  }, [products]);

  /**
   * Get products within a price range
   * @param {number} minPrice - Minimum price (inclusive)
   * @param {number} maxPrice - Maximum price (inclusive)
   * @returns {Array} Filtered products array
   */
  const getProductsByPriceRange = useCallback((minPrice, maxPrice) => {
    return products.filter(p => {
      const price = p.priceCAD || 0;
      return price >= minPrice && price <= maxPrice;
    });
  }, [products]);

  /**
   * Get unique categories from all products
   * @returns {Array} Array of unique category strings
   */
  const getCategories = useCallback(() => {
    const categories = products
      .map(p => p.category)
      .filter(Boolean) // Remove null/undefined
      .filter((v, i, arr) => arr.indexOf(v) === i); // Unique values
    
    return categories.sort();
  }, [products]);

  /**
   * Get unique subcategories for a specific category
   * @param {string} category - Category slug
   * @returns {Array} Array of unique subcategory strings
   */
  const getSubcategories = useCallback((category) => {
    const subcategories = products
      .filter(p => p.category?.toLowerCase() === category?.toLowerCase())
      .map(p => p.subcategory)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    
    return subcategories.sort();
  }, [products]);

  /**
   * Get similar products based on category, style, room, or material
   * @param {Object} product - Reference product
   * @param {number} limit - Maximum number of similar products to return
   * @returns {Array} Similar products array
   */
  const getSimilarProducts = useCallback((product, limit = 8) => {
    if (!product) return [];

    return products
      .filter(p => 
        p.id !== product.id && 
        (
          p.category === product.category ||
          p.style === product.style ||
          p.room === product.room ||
          p.material === product.material
        )
      )
      .slice(0, limit);
  }, [products]);

  /**
   * Get featured products (you can customize the logic)
   * Currently returns newest 4 products
   * @param {number} limit - Maximum number of featured products
   * @returns {Array} Featured products array
   */
  const getFeaturedProducts = useCallback((limit = 4) => {
    return getSortedProducts(products, 'newest').slice(0, limit);
  }, [products, getSortedProducts]);

  /**
   * Get products count by category
   * @returns {Object} Object with category as key and count as value
   */
  const getProductCountByCategory = useCallback(() => {
    const counts = {};
    products.forEach(p => {
      if (p.category) {
        counts[p.category] = (counts[p.category] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  // Return all state and helper functions
  return {
    // State
    products,
    allProducts,
    loading,
    error,
    
    // Single product
    getProductById,
    
    // Filtering
    getProductsByCategory,
    getInStockProducts,
    getOutOfStockProducts,
    getProductsByPriceRange,
    
    // Search
    searchProducts,
    
    // Sorting
    getSortedProducts,
    
    // Categories
    getCategories,
    getSubcategories,
    getProductCountByCategory,
    
    // Related products
    getSimilarProducts,
    getFeaturedProducts,
    
    // Convenience
    totalProducts: products.length,
    hasProducts: products.length > 0
  };
};

export default useProducts;
